import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  createPageMetadata,
  getStructuredData,
  professionalServiceSchema
} from '../seo/metadata';

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value));
};

const upsertCanonical = (href: string) => {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = href;
};

const setJsonLd = (schemas: unknown[]) => {
  document.head
    .querySelectorAll('script[data-route-jsonld]')
    .forEach((script) => script.remove());

  schemas.forEach((schema, index) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.routeJsonld = String(index);
    script.text = JSON.stringify(schema).replace(/</g, '\\u003c');
    document.head.appendChild(script);
  });
};

const RouteMetadata = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const metadata = createPageMetadata(pathname);
    document.documentElement.lang = 'nl';
    document.title = metadata.title;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: metadata.description
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: metadata.openGraph.title
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: metadata.openGraph.description
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: metadata.openGraph.url
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: metadata.openGraph.type
    });
    upsertMeta('meta[property="og:locale"]', {
      property: 'og:locale',
      content: metadata.openGraph.locale
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: metadata.openGraph.images[0].url
    });
    upsertMeta('meta[property="og:image:alt"]', {
      property: 'og:image:alt',
      content: metadata.openGraph.images[0].alt
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: metadata.twitter.card
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: metadata.twitter.title
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: metadata.twitter.description
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: metadata.twitter.images[0]
    });
    upsertCanonical(metadata.alternates.canonical);

    setJsonLd([
      professionalServiceSchema,
      ...metadata.schemas.map((schema) => getStructuredData(schema))
    ]);
  }, [pathname]);

  return null;
};

export default RouteMetadata;
